import React, { useState, useEffect } from 'react';
import { Save, Loader2, Globe, MapPin, Image } from 'lucide-react';
import { useApi } from '../AdminApp';

const HeroEditor = () => {
  const { apiCall, API_URL } = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('tr');
  const [message, setMessage] = useState({ type: '', text: '' });

  const [heroData, setHeroData] = useState({
    title: { tr: '', en: '', de: '' },
    subtitle: { tr: '', en: '', de: '' },
    description: { tr: '', en: '', de: '' },
    location: '',
    profileImage: ''
  });

  useEffect(() => {
    fetchHero();
  }, []);

  const fetchHero = async () => {
    try {
      const data = await apiCall('/hero');
      setHeroData(data);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await apiCall('/hero', {
        method: 'PUT',
        body: JSON.stringify(heroData)
      });
      setMessage({ type: 'success', text: 'Hero section updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value, lang = null) => {
    if (lang) {
      setHeroData(prev => ({
        ...prev,
        [field]: { ...prev[field], [lang]: value }
      }));
    } else {
      setHeroData(prev => ({ ...prev, [field]: value }));
    }
  };

  const tabs = [
    { id: 'tr', label: 'Türkçe', flag: '🇹🇷' },
    { id: 'en', label: 'English', flag: '🇺🇸' },
    { id: 'de', label: 'Deutsch', flag: '🇩🇪' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Hero Section</h1>
          <p className="text-gray-400 mt-1">Edit the main landing section of your portfolio</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-xl ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/50 text-green-400' : 'bg-red-500/10 border border-red-500/50 text-red-400'}`}>
          {message.text}
        </div>
      )}

      {/* Language Tabs */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden">
        <div className="flex border-b border-gray-700/50">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium transition ${
                activeTab === tab.id
                  ? 'text-white bg-gray-700/50 border-b-2 border-blue-500'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/30'
              }`}
            >
              <span>{tab.flag}</span>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Globe className="w-4 h-4 inline mr-2" />
              Title ({activeTab.toUpperCase()})
            </label>
            <input
              type="text"
              value={heroData.title[activeTab] || ''}
              onChange={(e) => handleChange('title', e.target.value, activeTab)}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              placeholder="Enter hero title..."
            />
          </div>

          {/* Subtitle */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Subtitle ({activeTab.toUpperCase()})
            </label>
            <input
              type="text"
              value={heroData.subtitle[activeTab] || ''}
              onChange={(e) => handleChange('subtitle', e.target.value, activeTab)}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              placeholder="Enter subtitle..."
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description ({activeTab.toUpperCase()})
            </label>
            <textarea
              value={heroData.description[activeTab] || ''}
              onChange={(e) => handleChange('description', e.target.value, activeTab)}
              rows={4}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
              placeholder="Enter description..."
            />
          </div>
        </div>
      </div>

      {/* Common Fields */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-6 space-y-6">
        <h3 className="text-lg font-semibold text-white">Common Settings</h3>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <MapPin className="w-4 h-4 inline mr-2" />
            Location
          </label>
          <input
            type="text"
            value={heroData.location || ''}
            onChange={(e) => handleChange('location', e.target.value)}
            className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            placeholder="Istanbul, Turkey"
          />
        </div>

        {/* Profile Image */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <Image className="w-4 h-4 inline mr-2" />
            Profile Image URL
          </label>
          <input
            type="text"
            value={heroData.profileImage || ''}
            onChange={(e) => handleChange('profileImage', e.target.value)}
            className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            placeholder="https://example.com/image.jpg"
          />
          {heroData.profileImage && (
            <div className="mt-4">
              <img
                src={heroData.profileImage.startsWith('/') ? `${API_URL.replace('/api', '')}${heroData.profileImage}` : heroData.profileImage}
                alt="Profile Preview"
                className="w-32 h-32 object-cover rounded-xl border border-gray-600"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroEditor;
